import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { DEMO } from '../../lib/demo';
import ConvAreaChart from '../../components/ConvAreaChart';
import { getCached, setCached } from '../../lib/pageCache';

// Série de démo (graphe « nouvelles conversations par jour ») — uniquement en mode démo.
const DEMO_CONV_SERIES = [
  { date: '2026-06-13', count: 22 }, { date: '2026-06-14', count: 18 }, { date: '2026-06-15', count: 27 },
  { date: '2026-06-16', count: 15 }, { date: '2026-06-17', count: 31 }, { date: '2026-06-18', count: 24 },
  { date: '2026-06-19', count: 38 }, { date: '2026-06-20', count: 29 }, { date: '2026-06-21', count: 19 },
  { date: '2026-06-22', count: 26 }, { date: '2026-06-23', count: 41 }, { date: '2026-06-24', count: 33 },
  { date: '2026-06-25', count: 28 }, { date: '2026-06-26', count: 45 },
];

// Tableau de bord agence (vue propriétaire/manager) — reproduit la maquette premium validée.
// Données de démo statiques ; en prod elles seront branchées sur les agrégats réels (CA, ventes,
// top chatteurs, santé des comptes). L'objectif ici = la structure + le rendu premium.

const euro = (n) => n.toLocaleString('fr-FR') + ' €';
// Objectif AUTO-CROISSANT : on vise toujours le palier rond juste au-dessus du CA actuel.
// Dès qu'un palier est atteint, l'objectif passe au suivant (10k → 20k → 30k → 100k…).
const GOAL_TIERS = [500, 1000, 2000, 3000, 5000, 10000, 20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000];
const nextGoal = (ca) => GOAL_TIERS.find((t) => t > ca) || (Math.ceil(ca / 100000) + 1) * 100000;
const MODELS = ['Toutes les modèles', 'Maya', 'Nova', 'Lia', 'Eva'];
const PERIODS = ['Hier', "Aujourd'hui", '7j', '30j', 'Mois'];
const MEDAL = ['#f5c451', '#cdd3e0', '#cd8b5a'];

function Ring({ pct }) {
  const C = 2 * Math.PI * 38;
  const off = C * (1 - pct / 100);
  return (
    <svg width="116" height="116" viewBox="0 0 116 116">
      <circle cx="58" cy="58" r="38" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="9" />
      <circle cx="58" cy="58" r="38" fill="none" stroke="url(#rg)" strokeWidth="9" strokeLinecap="round"
        strokeDasharray={C.toFixed(1)} strokeDashoffset={off.toFixed(1)} transform="rotate(-90 58 58)" />
      <defs><linearGradient id="rg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#3f7dff" /><stop offset="1" stopColor="#73a6ff" /></linearGradient></defs>
    </svg>
  );
}

function Area({ pts, values, labels, id }) {
  return (
    <svg viewBox="0 0 600 180" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="rgba(63,125,255,.3)" /><stop offset="1" stopColor="rgba(63,125,255,0)" /></linearGradient></defs>
      <line x1="0" y1="150" x2="600" y2="150" stroke="rgba(255,255,255,.08)" />
      <path d={`M0 ${pts[0][1]} ${pts.map((p) => `L${p[0]} ${p[1]}`).join(' ')} L600 150 L0 150 Z`} fill={`url(#${id})`} />
      <polyline points={pts.map((p) => `${p[0]},${p[1]}`).join(' ')} fill="none" stroke="#73a6ff" strokeWidth="2.6" strokeLinejoin="round" />
      {/* Chiffres affichés DIRECTEMENT au-dessus de chaque point (plus besoin de survoler).
          Les dates sont espacées (≈7 max) pour ne pas se chevaucher sur 30 jours. */}
      {Array.isArray(values) && (() => {
        const step = Math.max(1, Math.ceil(pts.length / 7));
        return pts.map((p, i) => (values[i] == null ? null : (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="3.2" fill="#73a6ff" />
            {values[i] > 0 && <text x={p[0]} y={Math.max(12, p[1] - 9)} textAnchor="middle" fontSize="12" fontWeight="700" fill="#dbe7ff">{values[i]}</text>}
            {labels && labels[i] && (i % step === 0 || i === pts.length - 1) && (
              <text x={p[0]} y="168" textAnchor="middle" fontSize="10" fill="rgba(255,255,255,.45)">{labels[i]}</text>
            )}
          </g>
        )));
      })()}
    </svg>
  );
}

const KPIS = [['Ventes', '142', '8%'], ['Conversations', '1 120', '5%'], ['Nouveaux fans', '318', '6%'], ['LTV / fan', '133 €', '4%']];
const CHATTERS = [['Lina', 1840, 100], ['Sam', 1460, 79], ['Max', 1180, 64], ['Tom', 1020, 55], ['Jade', 760, 41]];
const SALES = [['Yann B.', 'Lina', 69, '12 min'], ['Marc P.', 'Sam', 45, '38 min'], ['Théo L.', 'Max', 120, '1 h'], ['Renaud', 'Lina', 30, '2 h']];
const ACCTS = [['Maya', 671, 1], ['Nova', 382, 1], ['Lia', 59, 1], ['Eva', 5, 0]];
// Suivi des comptes (intégré dans le dashboard) : [nom, fans, nouvelles convs, CA 30j, taux DM, actif]
// [nom, spenders, nouveaux spenders, nouvelles convs, CA, actif]
const ACCT_TRACKING = [['Maya', 38, 6, 84, 2840, 1], ['Nova', 21, 3, 46, 1920, 1], ['Lia', 5, 1, 9, 410, 1], ['Eva', 0, 0, 1, 40, 0]];
const TRACK_KPIS = [['Revenus approuvés', '5 210 €', '11%'], ['Ventes', '140', '7%'], ['Nouvelles convs', '318', '6%'], ['Comptes actifs', '4', '']];

// Données par période [Hier, Aujourd'hui, 7j, 30j, Mois] — pilotées par la barre de période.
const PERIOD_CA = ['210 €', '340 €', '1 680 €', '6 480 €', '7 200 €'];
// CA encaissé via PayPal (reconstruit depuis les mails de notif PayPal) — le reste = DropFans.
const PERIOD_PAYPAL = [40, 60, 320, 1180, 1320];
const PERIOD_CA_LABEL = ['hier', "aujourd'hui", 'sur 7 jours', 'sur 30 jours', 'ce mois'];
const PERIOD_CA_TREND = ['▲ 4 % vs avant-hier', '▲ 6 % vs hier', '▲ 9 % vs semaine dernière', '▲ 12 % vs mois dernier', '▲ 12 % vs mois dernier'];
const PERIOD_KPIS = [
  [['Ventes', '6', '3%'], ['Conversations', '48', '2%'], ['Nouveaux fans', '12', '4%'], ['LTV / fan', '133 €', '1%']],
  [['Ventes', '9', '4%'], ['Conversations', '62', '3%'], ['Nouveaux fans', '18', '5%'], ['LTV / fan', '133 €', '2%']],
  [['Ventes', '38', '5%'], ['Conversations', '290', '4%'], ['Nouveaux fans', '74', '6%'], ['LTV / fan', '130 €', '3%']],
  [['Ventes', '142', '8%'], ['Conversations', '1 120', '5%'], ['Nouveaux fans', '318', '6%'], ['LTV / fan', '133 €', '4%']],
  [['Ventes', '156', '9%'], ['Conversations', '1 240', '6%'], ['Nouveaux fans', '342', '7%'], ['LTV / fan', '135 €', '4%']],
];
const PERIOD_CONV_CHART = [
  [[0, 90], [80, 70], [160, 80], [240, 60], [320, 75], [400, 50], [480, 65], [600, 55]],
  [[0, 80], [80, 95], [160, 70], [240, 85], [320, 60], [400, 75], [480, 55], [600, 50]],
  [[0, 120], [80, 110], [160, 115], [240, 90], [320, 100], [400, 80], [480, 85], [600, 70]],
  [[0, 120], [80, 104], [160, 110], [240, 82], [320, 90], [400, 64], [480, 58], [600, 44]],
  [[0, 130], [80, 115], [160, 120], [240, 95], [320, 105], [400, 75], [480, 68], [600, 50]],
];

export default function Dashboard() {
  const [period, setPeriod] = useState(1);  // démarre sur « Aujourd'hui »
  const [model, setModel] = useState(0);
  const [sort, setSort] = useState({ key: 3, dir: 'desc' });
  // PROD : vraies données depuis l'API. En DÉMO ces états restent inertes (les useEffect court-circuitent),
  // donc tout le rendu retombe sur les consts statiques de démo ci-dessus.
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [chatterStats, setChatterStats] = useState(null);  // modale stats au clic sur un top chatteur

  // PROD : liste réelle des comptes → onglets modèles.
  useEffect(() => {
    if (DEMO) return;
    api.get('/accounts').then((r) => setAccounts(r.data || [])).catch(() => {});
  }, []);

  // PROD : agrégats du dashboard pour la période + le compte sélectionnés.
  // Local-first : on réaffiche la dernière donnée connue (cache) INSTANTANÉMENT, puis on
  // rafraîchit en fond → plus d'écran de chargement en revenant sur la section.
  useEffect(() => {
    if (DEMO) return;
    const P = ['yesterday', 'today', '7d', '30d', 'month'][period];
    const aid = model > 0 && accounts[model - 1] ? accounts[model - 1].id : null;
    const key = `dashboard:${P}:${aid || ''}`;
    const cached = getCached(key);
    if (cached) setData(cached);
    api.get('/reports/dashboard?period=' + P + (aid ? '&account_id=' + aid : ''))
      .then((r) => { setData(r.data); setCached(key, r.data); })
      .catch(() => { if (!cached) setData(null); });
  }, [period, model, accounts]);
  const num = (v) => (typeof v === 'number' ? v : parseFloat(String(v).replace(/[^\d.,]/g, '').replace(',', '.')) || 0);
  const { agency, hasPerm } = useAuth();
  const isOwner = hasPerm('manage_telegram'); // manager = vue réduite (pas le CA agence)
  const navigate = useNavigate();
  const periodKey = ['yesterday', 'today', '7d', '30d', 'month'][period];
  // Clic sur un top chatteur → stats DIRECTEMENT dans une modale (plus de navigation lente).
  const openChatter = (id, name) => {
    if (!id) return;
    setChatterStats({ id, name, loading: true });
    api.get(`/reports/employee-stats/${id}?period=${periodKey}&tz_offset=${new Date().getTimezoneOffset()}`)
      .then((r) => setChatterStats({ id, name, ...r.data }))
      .catch(() => setChatterStats({ id, name, error: true }));
  };

  // live = on a de VRAIES données API (jamais en démo). Si null/erreur → fallback démo.
  // live = vraies données API complètes. On exige data.kpis (objet cœur) pour éviter
  // tout écran blanc si jamais l'API renvoyait un objet partiel.
  const live = !DEMO && data && data.kpis;

  // Onglets modèles : réels en prod, statiques en démo.
  const models = DEMO ? MODELS : ['Toutes les modèles', ...accounts.map((a) => a.name)];

  // Hero CA
  const caLabel = PERIOD_CA_LABEL[period];
  const caText = live ? euro(data.ca ?? 0) : (DEMO ? PERIOD_CA[period] : '—');
  const caNum = live ? (data.ca ?? 0) : (DEMO ? num(PERIOD_CA[period]) : 0);
  const goal = nextGoal(caNum);
  const goalPct = Math.min(100, Math.round((caNum / goal) * 100));

  // Sources de revenus
  const paypal = live ? (data.sources?.paypal ?? 0) : (DEMO ? PERIOD_PAYPAL[period] : 0);
  const dropfans = live ? (data.sources?.dropfans ?? 0) : (DEMO ? Math.max(0, caNum - paypal) : 0);
  const dfTotal = dropfans + paypal;
  const dfPct = dfTotal > 0 ? Math.round((dropfans / dfTotal) * 100) : 50;

  // KPIs : [label, valeur, tendance%]. En prod pas de tendance → '' (badge ▲ masqué).
  // KPIs voulus par Marlon : spenders (fans qui paient), nouveaux spenders, nouvelles
  // conversations. PAS de "Ventes" ni "LTV" ni "Conversations actives" ni total fans.
  const kpis = live
    ? [
        ['Spenders', String(data.kpis.spenders ?? 0), ''],
        ['Nouveaux spenders', String(data.kpis.new_spenders ?? 0), ''],
        ['Nouvelles conversations', String(data.kpis.new_convs ?? data.kpis.nouveaux_fans ?? 0), ''],
      ]
    : (DEMO
        ? [['Spenders', '48', ''], ['Nouveaux spenders', '12', ''], ['Nouvelles conversations', '34', '']]
        : [['Spenders', '—', ''], ['Nouveaux spenders', '—', ''], ['Nouvelles conversations', '—', '']]);

  // Graphe conversations : données brutes [{date, count}] → graphe recharts (ConvAreaChart).
  const convChartData = (live && Array.isArray(data.timeseries) && data.timeseries.length)
    ? data.timeseries
    : (DEMO ? DEMO_CONV_SERIES : []);

  // Top chatteurs : [nom, total€, %barre]. En prod normalise la barre sur le max.
  const chatters = (() => {
    if (!live || !Array.isArray(data.top_chatters)) return DEMO ? CHATTERS : [];
    const max = Math.max(1, ...data.top_chatters.map((c) => c.total || 0));
    return data.top_chatters.map((c) => [c.name, c.total || 0, Math.round(((c.total || 0) / max) * 100), c.id]);
  })();

  const ago = (iso) => { if (!iso) return ''; const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000); if (m < 1) return "à l'instant"; if (m < 60) return m + ' min'; const h = Math.floor(m / 60); if (h < 24) return h + ' h'; return Math.floor(h / 24) + ' j'; };
  const recentSales = live
    ? (data.recent_sales || []).map((s) => ({ fan: s.fan, chatter: s.chatter, bot: !!s.est_bot, amount: s.amount, label: ago(s.date) }))
    : (DEMO ? SALES.map((s) => ({ fan: s[0], chatter: s[1], amount: s[2], label: s[3] })) : []);
  // Suivi des comptes : [nom, fans, nouvelles convs, CA, taux DM, actif].
  const tracking = live
    ? (data.accounts || []).map((a) => [a.name, a.spenders || 0, a.new_spenders || 0, a.new_convs || 0, a.ca || 0, a.status === 'ready' || a.status === 'actif' || a.status === 'active' ? 1 : 0])
    : (DEMO ? ACCT_TRACKING : []);
  // Fans à relancer (baleines inactives) — owner + manager.
  const relancer = live ? (data.relancer || []) : [];
  // KPIs du suivi des comptes (live) : revenus, ventes, nouvelles convs, comptes actifs.
  const activeAccts = tracking.filter((a) => a[5]).length;
  const trackKpis = live
    ? [['Revenus approuvés', euro(data.ca), ''], ['Spenders', String(data.kpis.spenders ?? 0), ''], ['Nouveaux spenders', String(data.kpis.new_spenders ?? 0), ''], ['Comptes actifs', String(activeAccts), '']]
    : (DEMO ? TRACK_KPIS : [['Revenus approuvés', '—', ''], ['Spenders', '—', ''], ['Nouveaux spenders', '—', ''], ['Comptes actifs', String(activeAccts), '']]);
  const sortedTracking = [...tracking].sort((a, b) => (sort.dir === 'desc' ? num(b[sort.key]) - num(a[sort.key]) : num(a[sort.key]) - num(b[sort.key])));
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  const arrow = (key) => (sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <div className="p-6 max-w-[1440px] mx-auto" data-testid="agency-dashboard">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="text-sm uppercase tracking-wider text-slate-500 font-semibold">Tableau de bord</div>
        <h1 className="text-4xl font-extrabold text-white tracking-tight text-right truncate max-w-[60%]">{agency?.name || 'Mon agence'}</h1>
      </div>

      {/* filtres modèles + période */}
      <div className="flex gap-2.5 items-center flex-wrap mb-5">
        {models.map((mname, i) => (
          <button key={mname} onClick={() => setModel(i)}
            className={`px-3.5 h-8 rounded-full text-[13px] font-medium transition-colors ${i === model ? 'bg-white/10 text-white border border-white/20' : 'text-slate-400 hover:text-white border border-white/10'}`}>{mname}</button>
        ))}
        <span className="flex-1" />
        <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
          {PERIODS.map((p, i) => (
            <button key={p} onClick={() => setPeriod(i)}
              className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${i === period ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{p}</button>
          ))}
        </div>
      </div>

      {isOwner && (<>
      {/* hero CA */}
      <div className="rounded-2xl border border-white/10 p-6 flex items-center justify-between gap-6 mb-4"
        style={{ background: 'linear-gradient(135deg, rgba(63,125,255,.10), rgba(63,125,255,.015))' }}>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-400">Chiffre d'affaires · {caLabel}</div>
          <div className="text-5xl font-extrabold text-white mt-1 tracking-tight">{caText}</div>
          {!live && DEMO && <div className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 mt-3">{PERIOD_CA_TREND[period]}</div>}
          <svg viewBox="0 0 340 60" style={{ width: '100%', maxWidth: 400, height: 56, marginTop: 14 }}>
            <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="rgba(63,125,255,.35)" /><stop offset="1" stopColor="rgba(63,125,255,0)" /></linearGradient></defs>
            <path d="M0 44 L48 38 L96 41 L146 26 L194 31 L244 16 L292 12 L340 6 L340 60 L0 60 Z" fill="url(#sg)" />
            <polyline points="0,44 48,38 96,41 146,26 194,31 244,16 292,12 340,6" fill="none" stroke="#73a6ff" strokeWidth="2.4" />
          </svg>
        </div>
        <div className="flex flex-col items-center shrink-0">
          <div className="relative">
            <Ring pct={goalPct} />
            <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-white">{goalPct}%</div>
          </div>
          <div className="text-xs text-slate-500 mt-2">objectif {euro(goal)}</div>
        </div>
      </div>

      {/* Sources de revenus — DropFans + PayPal (notifs reconstruites depuis les mails) */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Sources de revenus · {caLabel}</h3>
          <span className="text-xs text-slate-500">DropFans + PayPal</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3f7dff' }} /> DropFans</div>
            <div className="text-2xl font-bold text-white">{euro(dropfans)}</div>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ef4444' }} /> PayPal</div>
            <div className="text-2xl font-bold text-white">{euro(paypal)}</div>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full overflow-hidden bg-white/5 flex">
          <div style={{ width: dfPct + '%', background: '#3f7dff' }} />
          <div style={{ width: (100 - dfPct) + '%', background: '#ef4444' }} />
        </div>
      </div>
      </>)}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {kpis.map((k) => (
          <div key={k[0]} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-xs text-slate-400">{k[0]}</div>
            <div className="text-3xl font-bold text-white mt-1">{k[1]}</div>
            {k[2] && <div className="inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 mt-2">▲ {k[2]}</div>}
          </div>
        ))}
      </div>

      {/* conversations + top chatteurs */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="font-semibold text-white">Nouvelles conversations par jour</h3>
          <div className="text-xs text-slate-500 mb-2">survole la courbe pour le détail · l'échelle et les dates sont affichées</div>
          <ConvAreaChart data={convChartData} unit="nouvelles convs" emptyText="Aucune nouvelle conversation sur cette période." />
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <h3 className="font-semibold text-white mb-4">Top chatteurs</h3>
          {chatters.length === 0 && <div className="text-sm text-slate-500 py-2">Aucune vente sur la période.</div>}
          {chatters.map((c, i) => (
            <div key={c[0]} onClick={() => openChatter(c[3], c[0])} title={c[3] ? 'Voir ses stats' : undefined} className={`mb-3 last:mb-0 ${c[3] ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-2 text-sm text-white">
                  {i < 3 && <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-black" style={{ background: MEDAL[i] }}>{i + 1}</span>}
                  {c[0]}
                </span>
                <b className="text-sm text-white">{euro(c[1])}</b>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: c[2] + '%', background: 'linear-gradient(90deg,#3f7dff,#73a6ff)' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* suivi des comptes — visible owner ET manager (seul le CA GLOBAL/hero reste owner-only) */}
      {(<>
      {/* suivi des comptes — intégré depuis l'ancienne page « Suivi des comptes » */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4">
        <h3 className="font-semibold text-white mb-4">Suivi des comptes</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {trackKpis.filter(k => isOwner || !String(k[0]).startsWith('Revenus')).map((k) => (
            <div key={k[0]} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <div className="text-[11px] text-slate-400">{k[0]}</div>
              <div className="text-xl font-bold text-white mt-0.5">{k[1]}</div>
              {k[2] && <div className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded bg-emerald-500/15 text-emerald-300 mt-1">▲ {k[2]}</div>}
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="text-left font-semibold py-2">Compte</th>
                <th onClick={() => toggleSort(1)} className="text-right font-semibold py-2 cursor-pointer hover:text-slate-300 select-none">Spenders{arrow(1)}</th>
                <th onClick={() => toggleSort(2)} className="text-right font-semibold py-2 cursor-pointer hover:text-slate-300 select-none">Nouveaux spenders{arrow(2)}</th>
                <th onClick={() => toggleSort(3)} className="text-right font-semibold py-2 cursor-pointer hover:text-slate-300 select-none hidden sm:table-cell">Nouvelles convs{arrow(3)}</th>
                <th onClick={() => toggleSort(4)} className="text-right font-semibold py-2 cursor-pointer hover:text-slate-300 select-none">CA · {PERIODS[period]}{arrow(4)}</th>
                <th className="text-right font-semibold py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {sortedTracking.map((a) => (
                <tr key={a[0]} className="border-b border-white/5 last:border-0">
                  <td className="py-2.5 font-medium text-white">{a[0]}</td>
                  <td className="py-2.5 text-right text-amber-300 font-semibold">{a[1]}</td>
                  <td className="py-2.5 text-right text-emerald-200">{a[2]}</td>
                  <td className="py-2.5 text-right text-slate-300 hidden sm:table-cell">{a[3]}</td>
                  <td className="py-2.5 text-right text-emerald-300 font-semibold">{Number(a[4]).toLocaleString('fr-FR')} €</td>
                  <td className="py-2.5 text-right"><span className="text-[11px] font-semibold" style={{ color: a[5] ? '#37d39a' : '#e8a13a' }}>{a[5] ? 'actif' : 'en chauffe'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ventes récentes (pleine largeur) */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="font-semibold text-white mb-3">Ventes récentes</h3>
        {recentSales.length === 0 ? <div className="text-sm text-slate-500 py-2">Aucune vente récente.</div> : recentSales.map((s, i) => (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
            {/* 14/10 (C-506) : « j'aimerais voir le petit truc Bot » sur une vente faite par le bot. */}
            <span className="text-sm"><b className="text-white">{s.fan}</b>{s.bot
              ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-md bg-violet-500/20 text-violet-200 border border-violet-500/30" data-testid="vente-recente-bot">🤖 Bot</span>
              : s.chatter ? <span className="text-slate-500"> · {s.chatter}</span> : null}</span>
            <span><span className="text-emerald-300 font-semibold">+{s.amount} €</span> <span className="text-slate-600 text-xs ml-1">{s.label}</span></span>
          </div>
        ))}
      </div>
      </>)}

      {/* Fans à relancer (baleines inactives) — TOUT EN BAS, sous Ventes récentes — owner ET manager */}
      {relancer.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-white">Fans à relancer 🐳</h3>
            <span className="text-xs text-slate-500">grosses dépenses · inactifs ≥ 7 j</span>
          </div>
          {relancer.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
              <span className="text-sm min-w-0 truncate">
                <b className="text-white">{r.name}</b>
                {r.model ? <span className="text-slate-500"> · {r.model}</span> : null}
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="text-emerald-300 font-semibold">{euro(r.spent)}</span>
                <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">inactif {r.days} j</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Analyse IA de tous les chatteurs — auto (en cache), tout en bas du tableau de bord */}

      {/* Stats d'un chatteur — modale au clic (direct, sans changer de page) */}
      {chatterStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setChatterStats(null)}>
          <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0e0f15] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Stats chatteur · {PERIODS[period]}</div>
                <h3 className="text-lg font-bold text-white">{chatterStats.name}</h3>
              </div>
              <button onClick={() => setChatterStats(null)} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            {chatterStats.loading ? (
              <div className="text-sm text-slate-400 py-8 text-center">Chargement des stats…</div>
            ) : chatterStats.error ? (
              <div className="text-sm text-rose-300 py-8 text-center">Impossible de charger les stats.</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    ['CA généré', euro(chatterStats.total_sales || 0)],
                    ['Ventes', String(chatterStats.sales_count || 0)],
                    ['Panier moyen', euro(chatterStats.avg_basket || 0)],
                    ['Messages', String(chatterStats.messages || 0)],
                    ['Liens envoyés', String(chatterStats.payment_links || 0)],
                    ['Conversion', (chatterStats.conversion_rate || 0) + ' %'],
                    ['Meilleure vente', euro(chatterStats.best_sale || 0)],
                    ['Temps de réponse (médian)', (() => { const v = chatterStats.median_response_seconds ?? chatterStats.avg_response_seconds; return v == null ? '—' : (v < 60 ? v + ' s' : (v < 3600 ? Math.round(v / 60) + ' min' : (v / 3600).toFixed(1) + ' h')); })()],
                    ['Série', (chatterStats.streak || 0) + ' j'],
                    ['En attente', euro(chatterStats.pending_total || 0)],
                  ].map((s) => (
                    <div key={s[0]} className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
                      <div className="text-[10px] text-slate-400">{s[0]}</div>
                      <div className="text-base font-bold text-white mt-0.5">{s[1]}</div>
                    </div>
                  ))}
                </div>

                {Array.isArray(chatterStats.daily) && chatterStats.daily.length > 0 && (() => {
                  const mx = Math.max(1, ...chatterStats.daily.map((d) => d.revenue || 0));
                  return (
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 mt-3">
                      <div className="text-[11px] text-slate-400 mb-2">7 derniers jours (CA)</div>
                      <div className="flex items-end gap-1.5 h-20">
                        {chatterStats.daily.map((d, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.label} · ${euro(d.revenue || 0)}`}>
                            <div className="w-full rounded-t" style={{ height: Math.max(2, Math.round((d.revenue || 0) / mx * 64)), background: 'linear-gradient(180deg,#73a6ff,#3f7dff)' }} />
                            <span className="text-[9px] text-slate-500">{d.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {Array.isArray(chatterStats.sales) && chatterStats.sales.length > 0 && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 mt-3 max-h-44 overflow-y-auto">
                    <div className="text-[11px] text-slate-400 mb-1">Ventes ({chatterStats.sales.length})</div>
                    {chatterStats.sales.slice(0, 30).map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0 text-sm">
                        <span className="text-white truncate">{s.fan}{s.type === 'donation' ? ' 🎁' : ''}</span>
                        <span className={s.status === 'approved' ? 'text-emerald-300 font-semibold' : 'text-amber-300'}>{euro(s.amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => { const id = chatterStats.id; setChatterStats(null); navigate('/dashboard/employees/report?chatter=' + id); }}
                  className="mt-4 w-full h-10 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm hover:bg-white/10 transition-colors">
                  Ouvrir le rapport employé complet →
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
